/* eslint-disable */
import React, { useState, useEffect } from 'react'
import {
    Dialog,
    DialogContent,
    DialogActions,
    Button,
    Typography,
    MobileStepper,
    Box,
    IconButton,
    useTheme,
    useMediaQuery,
} from '@mui/material'
import {
    KeyboardArrowLeft,
    KeyboardArrowRight,
    Close,
} from '@mui/icons-material'

const tutorialSteps = [
    {
        label: 'Dashboard / Course Home',
        description:
            'Welcome to your Dashboard! Here you can view all your enrolled courses and upcoming deadlines at a single glance.',
        imgPath: '/images/tutorial/dashboard.png', // Placeholder path
    },
    {
        label: 'Quiz & Assignment Interface',
        description:
            'Take interactive quizzes, submit your assignments, and receive real-time grading.',
        imgPath: '/images/tutorial/quiz.png',
    },
    {
        label: 'Grade Analyzer',
        description:
            'Easily track your academic progress, analyze your grades in real-time, and uncover insights to boost your performance.',
        imgPath: '/images/tutorial/grade_analyzer.png',
    },
    {
        label: 'Virtual TA',
        description:
            'Meet your AI-powered Virtual TA—ready to answer your questions and provide personalized academic support 24/7.',
        imgPath: '/images/tutorial/virtualta1.png',
    },
    {
        label: 'Modern User Profile',
        description:
            'Customize your public profile, showcase your skills, and build your digital resume.',
        imgPath: '/images/tutorial/profile.png',
    },
]

function TutorialModal() {
    const theme = useTheme()
    const fullScreen = useMediaQuery(theme.breakpoints.down('md'))
    const [open, setOpen] = useState(false)
    const [activeStep, setActiveStep] = useState(0)
    const [imageError, setImageError] = useState(false)
    const maxSteps = tutorialSteps.length

    useEffect(() => {
        // Check if the user has already seen the tutorial
        const hasSeenTutorial = localStorage.getItem('hasSeenTutorial')
        if (!hasSeenTutorial) {
            // Slight delay so the user sees the page first before the modal pops up
            const timer = setTimeout(() => {
                setOpen(true)
            }, 1000)
            return () => clearTimeout(timer)
        }
    }, [])

    useEffect(() => {
        setImageError(false)
    }, [activeStep])

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1)
    }

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1)
    }

    const handleClose = () => {
        localStorage.setItem('hasSeenTutorial', 'true')
        setOpen(false)
    }

    return (
        <Dialog
            fullScreen={fullScreen}
            open={open}
            onClose={handleClose}
            aria-labelledby="tutorial-dialog-title"
            PaperProps={{
                sx: {
                    borderRadius: fullScreen ? 0 : '16px',
                    maxWidth: '800px',
                    width: '100%',
                    overflow: 'hidden',
                },
            }}
        >
            <Box
                sx={{
                    position: 'relative',
                    bgcolor: 'var(--color-primary)',
                    color: '#ffffff',
                    p: 3,
                    textAlign: 'center',
                }}
            >
                <Typography
                    id="tutorial-dialog-title"
                    variant="h5"
                    fontWeight="bold"
                    sx={{ color: '#ffffff !important' }}
                >
                    {tutorialSteps[activeStep].label}
                </Typography>
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: 'absolute',
                        right: 8,
                        top: 8,
                    }}
                >
                    <Close sx={{ color: '#ffffff !important' }} />
                </IconButton>
            </Box>

            <DialogContent
                sx={{
                    p: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                }}
            >
                <Box
                    sx={{
                        width: '100%',
                        height: { xs: 250, sm: 350, md: 450 },
                        bgcolor: 'var(--color-surface-2)',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        position: 'relative',
                        overflow: 'hidden',
                    }}
                >
                    {imageError ? (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                color: 'var(--color-text-muted)',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: '3rem',
                                    marginBottom: '10px',
                                }}
                            >
                                📸
                            </div>
                            <div style={{ fontWeight: 'bold' }}>
                                Screenshot Placeholder
                            </div>
                            <div
                                style={{ fontSize: '0.8rem', marginTop: '5px' }}
                            >
                                Please add screenshot to{' '}
                                {tutorialSteps[activeStep].imgPath}
                            </div>
                        </div>
                    ) : (
                        <Box
                            component="img"
                            src={tutorialSteps[activeStep].imgPath}
                            alt={tutorialSteps[activeStep].label}
                            sx={{
                                maxWidth: '100%',
                                maxHeight: '100%',
                                objectFit: 'contain',
                            }}
                            onError={() => setImageError(true)}
                        />
                    )}
                </Box>
                <Box sx={{ p: 4, textAlign: 'center', minHeight: '120px' }}>
                    <Typography
                        variant="body1"
                        sx={{
                            fontSize: '1.1rem',
                            color: 'var(--color-text)',
                            lineHeight: 1.6,
                        }}
                    >
                        {tutorialSteps[activeStep].description}
                    </Typography>
                </Box>
            </DialogContent>

            <DialogActions
                sx={{
                    justifyContent: 'space-between',
                    p: 3,
                    borderTop: '1px solid var(--color-border)',
                }}
            >
                <Button
                    size="large"
                    onClick={handleBack}
                    disabled={activeStep === 0}
                    startIcon={<KeyboardArrowLeft />}
                    sx={{ fontWeight: 'bold' }}
                >
                    Back
                </Button>

                <MobileStepper
                    steps={maxSteps}
                    position="static"
                    activeStep={activeStep}
                    sx={{
                        bgcolor: 'transparent',
                        flexGrow: 1,
                        justifyContent: 'center',
                        '& .MuiMobileStepper-dotActive': {
                            backgroundColor: 'var(--color-accent)',
                            transform: 'scale(1.3)',
                        },
                    }}
                    nextButton={null}
                    backButton={null}
                />

                {activeStep === maxSteps - 1 ? (
                    <Button
                        size="large"
                        variant="contained"
                        onClick={handleClose}
                        sx={{
                            bgcolor: 'var(--color-primary)',
                            '&:hover': {
                                bgcolor: 'var(--color-primary-hover)',
                            },
                            fontWeight: 'bold',
                            px: 4,
                        }}
                    >
                        Get Started
                    </Button>
                ) : (
                    <Button
                        size="large"
                        variant="contained"
                        onClick={handleNext}
                        endIcon={<KeyboardArrowRight />}
                        sx={{
                            bgcolor: 'var(--color-primary)',
                            '&:hover': {
                                bgcolor: 'var(--color-primary-hover)',
                            },
                            fontWeight: 'bold',
                        }}
                    >
                        Next
                    </Button>
                )}
            </DialogActions>
        </Dialog>
    )
}

export default TutorialModal
